// Подтверждение доставки сообщений (устойчивость к флапам сети и полумёртвым
// iOS-сокетам, у которых client.connected=true, а пакеты уходят в никуда).
//
// Схема: publish qos1 (PUBACK от брокера) → ожидание ACK от BACKEND'а — любого
// status/response по requestId (бэкенд публикует received сразу после валидации) или
// присутствия запроса в /api/activity. Нет ACK — тихие ре-publish тем же requestId
// (бэкенд дедуплицирует по (chatId, requestId) и на дубликат replay'ит готовый
// ответ, поэтому повторы безопасны). Исчерпание попыток → «Не доставлено» +
// кнопка «Повторить» на сообщении (deliveryStore → Message.svelte).
//
// Импорты: mqtt.js — ТОЛЬКО динамически (тяжёлый ленивый чанк; к моменту отправки он
// загружен). stores.js этот модуль импортирует статически — обратного импорта нет.

import { writable } from 'svelte/store';

// Ожидания ПОСЛЕ n-й попытки publish: 10с на первый ACK (очередь бэкенда строго
// последовательна — перед нами может стоять чужой запрос с LLM-вызовами), затем
// паузы перед ре-publish 5/10/20с. Итого 4 publish и ~45с до вердикта «не доставлено».
const ACK_WAITS_MS = [10000, 5000, 10000, 20000];
const PUBACK_TIMEOUT_MS = 5000;
const MAX_PENDING = 20;
const PENDING_MAX_AGE_MS = 10 * 60 * 1000;
const PERSIST_KEY = 'petrovich:pending-requests';

// requestId → 'queued' | 'sending' | 'sent' | 'failed' | 'photo-lost' — реактивное состояние для UI.
// 'photo-lost' (К5, 1.27.7): сообщение с фото не пережило перезагрузку — картинки в
// очереди не хранятся, повторить нечего; UI предлагает «Прикрепить заново».
// 'queued' — офлайн-очередь: браузер без сети, сообщение ждёт восстановления связи
// (промоушен в 'sending' делает flushPending на MQTT-реконнекте/resume).
// Отдельный стор, а не поле сообщения: mergeMessageLists пересоздаёт объекты.
export const deliveryStore = writable({});

const pending = new Map();

const loadMqtt = () => import('./mqtt.js');

// Резолвер модели по умолчанию (ставит stores.js в initApp): в офлайне модель может
// быть ещё не загружена (cold-start без сети) — доставка подставит её при отправке.
let modelResolver = null;

export function setModelResolver(resolver) {
  modelResolver = resolver;
}

function setState(requestId, state) {
  deliveryStore.update((current) => ({ ...current, [requestId]: state }));
}

function clearTimer(entry) {
  if (entry.timer) {
    globalThis.clearTimeout(entry.timer);
    entry.timer = null;
  }
}

function buildPubackTimeout() {
  return new Promise((_, reject) => {
    globalThis.setTimeout(() => {
      const error = new Error('PUBACK timeout');
      error.code = 'PUBACK_TIMEOUT';
      reject(error);
    }, PUBACK_TIMEOUT_MS);
  });
}

function persistPending() {
  try {
    // Персистим только текст (images после reload не восстановить из localStorage —
    // квота; осознанное ограничение: их доставка не переживает перезагрузку страницы).
    const items = [...pending.values()]
      .filter((entry) => !entry.payload.imageDataUrls?.length)
      .map((entry) => ({
        requestId: entry.payload.requestId,
        chatId: entry.payload.chatId,
        model: entry.payload.model,
        text: entry.payload.text,
        createdAt: entry.createdAt,
      }));
    if (items.length) {
      window.localStorage.setItem(PERSIST_KEY, JSON.stringify(items));
    } else {
      window.localStorage.removeItem(PERSIST_KEY);
    }
  } catch (_) {
    // best effort (квота/приватный режим)
  }
}

// Записи старше 10 минут: принудительно failed, тяжёлые images освобождаем. Очередь
// 'queued' по возрасту НЕ выселяется — долгий офлайн не должен убивать сообщения; их
// 10 минут стартуют с промоушена в 'sending' (createdAt обновляется в flushPending).
// Запись без картинок retryNow уже не повторяет (К5, 1.27.7): раньше «Повторить»
// молча слал один текст — теперь экран шлёт сообщение заново с картинками пузыря.
function pruneExpired() {
  const now = Date.now();
  for (const entry of pending.values()) {
    if (now - entry.createdAt > PENDING_MAX_AGE_MS && entry.state === 'sending') {
      markFailed(entry);
      if (entry.payload.imageDataUrls?.length) {
        entry.imagesDropped = true;
      }
      entry.payload.imageDataUrls = [];
    }
  }
}

function enforceCap() {
  pruneExpired();
  if (pending.size < MAX_PENDING) {
    return;
  }
  // Выселяем самые старые записи (сперва failed, затем прочие).
  const entries = [...pending.values()].sort((a, b) => a.createdAt - b.createdAt);
  for (const entry of entries) {
    if (pending.size < MAX_PENDING) {
      break;
    }
    clearTimer(entry);
    pending.delete(entry.payload.requestId);
  }
}

function markFailed(entry) {
  clearTimer(entry);
  entry.state = 'failed';
  setState(entry.payload.requestId, 'failed');
  persistPending();
}

async function attempt(entry) {
  const { requestId } = entry.payload;
  if (!pending.has(requestId) || entry.state !== 'sending') {
    return;
  }
  // Модель могла быть неизвестна при постановке в офлайн-очередь — резолвим при
  // отправке; всё ещё нет (настройки не загрузились) — возвращаем в очередь без
  // сжигания попытки, следующий flushPending попробует снова.
  if (!entry.payload.model && modelResolver) {
    entry.payload.model = modelResolver() || null;
  }
  if (!entry.payload.model) {
    entry.state = 'queued';
    setState(requestId, 'queued');
    persistPending();
    return;
  }
  const { chatId, model, text, imageDataUrls, includeStages } = entry.payload;
  entry.attempts += 1;
  clearTimer(entry);
  try {
    const mqtt = await loadMqtt();
    const meta = includeStages?.length ? { imageDataUrls, includeStages } : { imageDataUrls };
    await Promise.race([
      mqtt.publishRequest(requestId, chatId, model, text, meta),
      buildPubackTimeout(),
    ]);
  } catch (error) {
    // Не connected — подождём reconnected/flushPending. PUBACK-таймаут при живом
    // client.connected — сигнатура полумёртвого сокета: один раз на цикл форсируем
    // пересоздание соединения (контролируемо, как вотчдог).
    if (error?.code === 'PUBACK_TIMEOUT' && !entry.forcedReconnect) {
      entry.forcedReconnect = true;
      loadMqtt().then((m) => m.forceReconnect()).catch(() => {});
    }
  }
  scheduleNext(entry);
}

function scheduleNext(entry) {
  const { requestId } = entry.payload;
  if (!pending.has(requestId) || entry.state !== 'sending') {
    return;
  }
  const wait = ACK_WAITS_MS[entry.attempts - 1];
  if (wait == null) {
    markFailed(entry);
    return;
  }
  entry.timer = globalThis.setTimeout(() => {
    entry.timer = null;
    if (entry.attempts >= ACK_WAITS_MS.length) {
      markFailed(entry);
      return;
    }
    attempt(entry);
  }, wait);
}

// Регистрирует запрос и запускает цикл доставки. Никогда не бросает: при
// заведомом офлайне браузера сообщение встаёт в очередь ('queued') и уйдёт
// автоматически, когда flushPending промоутит его после восстановления связи.
// При «нет MQTT, но сеть есть» — обычный цикл: запрос ждёт reconnected/ре-publish.
export function sendWithDelivery({ requestId, chatId, model, text, imageDataUrls = [], includeStages = [] }) {
  const browserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  enforceCap();
  const entry = {
    // includeStages (этап 23, реш. 22): этапы, вложенные в запрос целиком — один раз.
    payload: { requestId, chatId, model, text, imageDataUrls, includeStages },
    attempts: 0,
    timer: null,
    createdAt: Date.now(),
    forcedReconnect: false,
    state: browserOffline ? 'queued' : 'sending',
  };
  pending.set(requestId, entry);
  setState(requestId, entry.state);
  persistPending();
  if (!browserOffline) {
    attempt(entry);
  }
}

// ACK от бэкенда (любой status/response по requestId или /api/activity):
// запрос доставлен, снимаем ожидание и освобождаем payload (в т.ч. images).
export function acknowledge(requestId) {
  const entry = pending.get(requestId);
  if (!entry) {
    return;
  }
  clearTimer(entry);
  pending.delete(requestId);
  setState(requestId, 'sent');
  persistPending();
}

// Кнопка «Повторить» на недоставленном сообщении: свежий цикл тем же requestId.
// false — записи уже нет (сирота после reload): вызывающий отправляет заново.
export function retryNow(requestId) {
  const entry = pending.get(requestId);
  if (!entry || entry.imagesDropped) {
    return false;
  }
  entry.attempts = 0;
  entry.forcedReconnect = false;
  entry.state = 'sending';
  entry.createdAt = Date.now();
  setState(requestId, 'sending');
  attempt(entry);
  return true;
}

// Есть ли активная запись доставки (очередь/отправка/failed с payload'ом).
export function hasPending(requestId) {
  return pending.has(requestId);
}

// Есть ли текстовая запись в персисте (переживёт reload и будет восстановлена
// на первом MQTT-connect) — сироты гидрации сверяются и с ней.
export function hasPersistedPending(requestId) {
  try {
    const items = JSON.parse(window.localStorage.getItem(PERSIST_KEY) || '[]');
    return items.some((item) => item?.requestId === requestId);
  } catch (_) {
    return false;
  }
}

// Пометить сироту: оптимистичное сообщение из снапшота, чья доставка не пережила
// reload (например, очередь с картинками). UI покажет «Не доставлено» + «Повторить».
export function flagOrphanFailed(requestId) {
  if (!pending.has(requestId)) {
    setState(requestId, 'failed');
  }
}

// Сирота с фото (К5, 1.27.7): картинки не пережили reload — повторять нечего,
// UI предлагает «Фото не сохранилось · Прикрепить заново».
export function flagOrphanPhotoLost(requestId) {
  if (!pending.has(requestId)) {
    setState(requestId, 'photo-lost');
  }
}

// Пузырь снят (прикрепили фото заново) — состояние доставки больше не нужно.
export function forgetDelivery(requestId) {
  const entry = pending.get(requestId);
  if (entry) {
    clearTimer(entry);
    pending.delete(requestId);
    persistPending();
  }
  deliveryStore.update((current) => {
    if (!(requestId in current)) {
      return current;
    }
    const { [requestId]: _removed, ...rest } = current;
    return rest;
  });
}

// Успешный MQTT-реконнект/resume: немедленный ре-publish всех ожидающих (attempts
// НЕ сбрасывается — на флапающей сети цикл всё равно конечен до «Не доставлено»).
// Офлайн-очередь ('queued') промоутится в свежий цикл доставки: attempts=0 и новый
// createdAt — её 10 минут жизни отсчитываются от восстановления связи.
export function flushPending() {
  pruneExpired();
  for (const entry of pending.values()) {
    if (entry.state === 'queued') {
      entry.state = 'sending';
      entry.attempts = 0;
      entry.forcedReconnect = false;
      entry.createdAt = Date.now();
      setState(entry.payload.requestId, 'sending');
      clearTimer(entry);
      attempt(entry);
    } else if (entry.state === 'sending') {
      clearTimer(entry);
      attempt(entry);
    }
  }
  persistPending();
}

// После reload (iOS-resume может перезагрузить страницу): один авто-ре-publish
// текстовых pending из localStorage. Сверка с сервером не нужна — бэкенд
// дедуплицирует и на дубликат сам вернёт состояние/готовый ответ.
export function restorePersistedPending() {
  let items = [];
  try {
    items = JSON.parse(window.localStorage.getItem(PERSIST_KEY) || '[]');
  } catch (_) {
    return;
  }
  const now = Date.now();
  // При рестарте в офлайне восстановленные записи идут в очередь ('queued') и
  // дождутся связи (возрастной лимит к очереди не применяется — её 10 минут
  // начнутся с промоушена); онлайн — один шанс (последняя попытка цикла).
  const browserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  for (const item of items) {
    if (!item?.requestId || pending.has(item.requestId)) {
      continue;
    }
    if (!browserOffline && now - (item.createdAt || 0) > PENDING_MAX_AGE_MS) {
      continue;
    }
    const entry = {
      payload: {
        requestId: item.requestId,
        chatId: item.chatId,
        model: item.model,
        text: item.text,
        imageDataUrls: [],
      },
      attempts: browserOffline ? 0 : ACK_WAITS_MS.length - 1,
      timer: null,
      createdAt: item.createdAt || now,
      forcedReconnect: false,
      state: browserOffline ? 'queued' : 'sending',
    };
    pending.set(item.requestId, entry);
    setState(item.requestId, entry.state);
    if (!browserOffline) {
      attempt(entry);
    }
  }
}

export function clear() {
  for (const entry of pending.values()) {
    clearTimer(entry);
  }
  pending.clear();
  deliveryStore.set({});
  try {
    window.localStorage.removeItem(PERSIST_KEY);
  } catch (_) {
    // best effort
  }
}

// Для тестов: текущее количество ожидающих.
export function pendingCount() {
  return pending.size;
}
